import React, { useState, useEffect } from "react";
import {
    Card,
    Form,
    Input,
    Button,
    message,
    Breadcrumb,
    Divider,
    Row,
    Col,
    Spin,
    Space,
} from "antd";
import {
    SaveOutlined,
    ArrowLeftOutlined,
    HomeOutlined,
    DollarOutlined,
    TagOutlined,
    EditOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { cnfAPIWithErrorHandling } from "../../../api/invoice/cnf";

export default function InvoiceCnfTemplate() {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [initialData, setInitialData] = useState(null);

    const navigate = useNavigate();
    const { id } = useParams(); // Lấy ID của mẫu từ URL

    // --- LOGIC FETCH DỮ LIỆU ---
    useEffect(() => {
        const fetchCnfData = async () => {
            if (!id) {
                message.error("Thiếu ID cấu hình tài khoản xuất hóa đơn.");
                setLoading(false);
                return;
            }

            try {
                // Giả định API GET chi tiết mẫu theo ID
                const response = await cnfAPIWithErrorHandling.getCnfById(id);

                if (response.data && response.data.success) {
                    const data = response.data.data;
                    const normalizedData = {
                        ...data,
                    };

                    setInitialData(normalizedData);
                    form.setFieldsValue(normalizedData);
                    message.info(`Đã tải cấu hình tài khoản xuất hóa đơn ID: ${id}`);
                } else {
                    throw new Error(response.data?.message || "Không tìm thấy cấu hình tài khoản xuất hóa đơn.");
                }
            } catch (error) {
                message.error(error.message || "Lỗi tải dữ liệu cấu hình tài khoản xuất hóa đơn.");
                navigate("/fee-templates"); // Quay lại trang danh sách nếu lỗi
            } finally {
                setLoading(false);
            }
        };

        fetchCnfData();
    }, [id, form, navigate]);

    // --- HÀM SUBMIT CẬP NHẬT ---
    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            // Chuẩn bị dữ liệu cho API cập nhật
            const dataToUpdate = {
                ...values,
                id: id,
            };

            // API call to update template
            const response = await cnfAPIWithErrorHandling.updateCnf(id, dataToUpdate);

            if (response.data && response.data.success) {
                message.success("Cập nhật cấu hình tài khoản thành công!");
            } else {
                throw new Error(response.data?.message || "Lỗi không xác định khi cập nhật.");
            }

            // Có thể navigate về trang chi tiết hoặc danh sách
            navigate("/invoice-accounts");
        } catch (error) {
            message.error(error.message || "Không thể cập nhật cấu hình tài khoản");
            console.error("Error updating template:", error);
        } finally {
            setSubmitting(false);
        }
    };

    // Các hàm khác
    const handleReset = () => { form.setFieldsValue(initialData); message.info("Đã khôi phục dữ liệu ban đầu"); };
    const handleBack = () => { navigate("/invoice-accounts"); message.info("Quay lại danh sách cấu hình tài khoản"); };

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center"
                style={{
                display: "flex",
                justifyContent: "center",
                height: "80vh",
                alignItems: "center",
                }}>
                <Spin size="large" tip="Đang tải dữ liệu tài khoản..." />
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            <Card className="shadow-lg max-w-7xl mx-auto">
                {/* Breadcrumb */}
                <div className="mb-6">
                    <Breadcrumb
                        separator=">"
                        className="mb-4"
                        items={[
                            { href: "/", title: (<><HomeOutlined /> Trang chủ</>) },
                            { href: "/invoice-accounts", title: (<><DollarOutlined /> Tài khoản xuất hóa đơn</>) },
                            { title: (<><EditOutlined /> Chỉnh sửa</>) },
                        ]}
                    />
                </div>

                <Divider />

                {/* Form */}
                <Spin spinning={submitting} tip="Đang cập nhật thông tin...">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        requiredMark="optional"
                        className="space-y-4"
                    >
                        <Row gutter={24}>
                            {/* CỘT THÔNG TIN CHUNG */}
                            <Col xs={24} lg={24}>
                                <Card
                                    title={
                                        <span className="text-blue-600">
                                            <TagOutlined className="mr-2" />
                                            Thông tin chung
                                        </span>
                                    }
                                    className="h-full shadow-sm border-blue-200"
                                >
                                    <Form.Item
                                        name="name"
                                        label={<span className="font-medium">Tên cấu hình (*)</span>}
                                        rules={[
                                            { required: true, message: "Vui lòng nhập tên cấu hình!" },
                                            { max: 255, message: "Tên mẫu không được vượt quá 255 ký tự!" },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Nhập tên cấu hình"
                                            size="default"
                                            className="rounded-lg"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="config_code"
                                        label={<span className="font-medium">Code cấu hình (*)</span>}
                                        rules={[
                                            { required: true, message: "Vui lòng nhập mã code cấu hình!" },
                                            { max: 255, message: "Code mẫu không được vượt quá 255 ký tự!" },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Nhập code cấu hình"
                                            size="default"
                                            className="rounded-lg"
                                        />
                                    </Form.Item>
                                </Card>
                            </Col>
                        </Row>

                        {/* Action Buttons */}
                        <Card className="mt-6 shadow-sm bg-gradient-to-r from-blue-50 to-green-50">
                            <Row
                                justify="space-between"
                                align="middle"
                            >
                                <Col>
                                    <Button
                                        icon={<ArrowLeftOutlined />}
                                        onClick={handleBack}
                                        size="default"
                                        className="shadow-sm"
                                    >
                                        Quay lại
                                    </Button>
                                </Col>
                                <Col>
                                    <Space size="small">
                                        <Button
                                            type="default"
                                            size="default"
                                            onClick={handleReset}
                                            className="min-w-32 rounded-lg shadow-sm"
                                        >
                                            Khôi phục
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="default"
                                            htmlType="submit"
                                            loading={submitting}
                                            icon={<SaveOutlined />}
                                            className="min-w-40 rounded-lg shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 border-none"
                                        >
                                            {submitting ? "Đang cập nhật..." : "Lưu thay đổi"}
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Card>
                    </Form>
                </Spin>
            </Card>
        </div>
    );
}